import { ContractStatus } from '@/gql/graphql'

export type ContractBucket = 'scheduled' | 'draft' | 'active' | 'archived'

export type ContractChangeField = 'rate' | 'startDate' | 'endDate'

export interface ContractHistoryEntry {
  id: string
  status: ContractStatus
  createdAt: string
  workerAcceptedAt?: string | null
  termsAcceptedAt?: string | null
  effectiveAt?: string | null
  startDate?: string | null
  endDate?: string | null
  rate?: number | null
  rateType?: string | null
  currency?: string | null
  pdfUrl?: string | null
}

export interface ContractHistoryStep {
  contract: ContractHistoryEntry
  versionNumber: number
  isOriginal: boolean
  isLatest: boolean
  bucket: ContractBucket
  groupCount: number
  isFirstOfGroup: boolean
  hasPdf: boolean
  effectiveFrom: string | null
  scheduleLapsed: boolean
  previous: ContractHistoryEntry | null
  changes: ContractChangeField[]
}

const BUCKET_ORDER: ContractBucket[] = ['scheduled', 'draft', 'active', 'archived']

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function dateOnly(value?: string | null): string | null {
  return value ? value.slice(0, 10) : null
}

function effectiveStartOf(contract: ContractHistoryEntry): string | null {
  return dateOnly(
    contract.effectiveAt ??
      contract.termsAcceptedAt ??
      contract.workerAcceptedAt ??
      contract.createdAt,
  )
}

function effectiveFromFor(contract: ContractHistoryEntry, bucket: ContractBucket): string | null {
  if (bucket === 'active' || bucket === 'archived') {
    return effectiveStartOf(contract)
  }
  return dateOnly(contract.effectiveAt)
}

function changesFrom(
  previous: ContractHistoryEntry | null,
  contract: ContractHistoryEntry,
): ContractChangeField[] {
  if (previous === null) {
    return []
  }

  const changes: ContractChangeField[] = []
  if (
    previous.rate !== contract.rate ||
    previous.currency !== contract.currency ||
    previous.rateType !== contract.rateType
  ) {
    changes.push('rate')
  }
  if (dateOnly(previous.startDate) !== dateOnly(contract.startDate)) {
    changes.push('startDate')
  }
  if (dateOnly(previous.endDate) !== dateOnly(contract.endDate)) {
    changes.push('endDate')
  }

  return changes
}

function bucketFor(contract: ContractHistoryEntry): ContractBucket {
  if (contract.status === ContractStatus.Active) return 'active'
  if (contract.status === ContractStatus.Archived) return 'archived'

  return contract.effectiveAt ? 'scheduled' : 'draft'
}

function scheduleHasLapsed(contract: ContractHistoryEntry, bucket: ContractBucket): boolean {
  if (bucket !== 'scheduled' || contract.workerAcceptedAt) {
    return false
  }
  const effectiveAt = dateOnly(contract.effectiveAt)
  return effectiveAt !== null && effectiveAt < today()
}

export function buildContractHistory(
  contracts: readonly ContractHistoryEntry[],
): ContractHistoryStep[] {
  const chronological = [...contracts].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
  const versionOf = new Map<string, number>()
  chronological.forEach((contract, index) => versionOf.set(contract.id, index + 1))

  const bucketOf = new Map<string, ContractBucket>()
  for (const contract of contracts) {
    bucketOf.set(contract.id, bucketFor(contract))
  }
  const byBucket = new Map<ContractBucket, ContractHistoryEntry[]>()
  for (const contract of contracts) {
    const bucket = bucketOf.get(contract.id)!
    const list = byBucket.get(bucket) ?? []
    list.push(contract)
    byBucket.set(bucket, list)
  }

  const steps: ContractHistoryStep[] = []
  for (const bucket of BUCKET_ORDER) {
    const items = byBucket.get(bucket)
    if (!items || items.length === 0) continue

    const ordered = [...items].sort(
      (a, b) => (versionOf.get(b.id) ?? 0) - (versionOf.get(a.id) ?? 0),
    )

    ordered.forEach((contract, indexInGroup) => {
      const versionNumber = versionOf.get(contract.id) ?? 0
      const previous = chronological[versionNumber - 2] ?? null
      steps.push({
        contract,
        versionNumber,
        isOriginal: versionNumber === 1,
        isLatest: versionNumber === chronological.length,
        bucket,
        groupCount: items.length,
        isFirstOfGroup: indexInGroup === 0,
        hasPdf: Boolean(contract.pdfUrl),
        effectiveFrom: effectiveFromFor(contract, bucket),
        scheduleLapsed: scheduleHasLapsed(contract, bucket),
        previous,
        changes: changesFrom(previous, contract),
      })
    })
  }

  return steps
}

export function canViewContractHistory(options: {
  viewerIsSupplier: boolean
  recruiterManagesWorkers: boolean
  contractCount: number
}): boolean {
  const isNonManagingSupplier = options.viewerIsSupplier && !options.recruiterManagesWorkers

  return !isNonManagingSupplier && options.contractCount > 0
}
